/**
 * Hook para lógica de salvamento de contagens
 *
 * Responsabilidades:
 * - Validação de campos obrigatórios
 * - Epic 46: Validação de intervalo de agendamento
 * - Criação/Atualização de jobs
 * - Mensagens de erro/sucesso
 * - Navegação após salvar
 *
 * NOTA: Este hook usa useRef para sempre acessar valores atuais do config,
 * evitando problemas de stale closure com useCallback.
 * Lógica replicada do commit 6208546d612ea752951794a2eed15d20e3df1e96
 */
import { useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import jobService from '../services/jobService';
import clienteService from '../services/clienteService';
import type { ValidarIntervaloResponse } from '../services/clienteService';
import { msg, MESSAGES } from '../utils/messages';
import { clientesParaValidarIntervalo } from '../utils/intervaloAgendamento';
import { STATUS_MAP_REVERSE } from '../constants/status';
import type { User } from '../services/userService';
import type { LocalContagem } from '../types/estabelecimento';
import type { ClienteLocaisPayload } from '../types/contagemLocais';
import type { JobFormData } from './useContagemJobLoader';
import type { JustificativaIntervalo } from '../components/IntervaloAgendamentoModal';

export interface UseContagemSaveReturn {
  handleSave: () => Promise<void>;
  handleDelete: (confirm: (opts: any) => Promise<boolean>) => Promise<void>;
  // Epic 46: Modal de validação de intervalo de agendamento
  showIntervaloModal: boolean;
  intervaloValidacoes: ValidarIntervaloResponse[];
  intervaloDataSelecionada: string;
  intervaloLoading: boolean;
  handleIntervaloConfirm: () => Promise<void>;
  // Epic 47: Handler que aceita justificativas do modal
  handleIntervaloConfirmWithJustificativas: (justificativas: JustificativaIntervalo[]) => Promise<void>;
  handleIntervaloCancel: () => void;
}

export interface ContagemSaveConfig {
  id: string | undefined;
  isNewContagem: boolean;
  formData: JobFormData;
  selectedClienteIds: number[];
  selectedHospitalId: number;
  originalHospitalId: number | null;
  // N165: para decidir SE o alerta de intervalo deve aparecer nesta gravação.
  // Nenhum estado novo — os dois já existem na página:
  //  - `modifiedFields`: a aba de Agendamento já pinta a borda de laranja com
  //    `modifiedFields.has('dataHora')`; só não chegava até aqui.
  //  - `originalFormData`: snapshot tirado ao entrar em modo de edição
  //    (useContagemPageEffects:208), que já carrega `selectedClienteIds` e
  //    `selectedHospitalId`.
  modifiedFields: Set<string>;
  originalFormData: any;
  legacyLocationId: number | null;
  auditoresParticipantes: User[];
  locaisContagemSelecionados: LocalContagem[];
  // Epic LOCAIS-POR-CLIENTE: optional grouped payload from new hook.
  // When present, takes precedence over `locaisContagemSelecionados`.
  clienteLocaisPayload?: ClienteLocaisPayload[];
  allLocaisFlat?: LocalContagem[];
  setIsEditMode: (mode: boolean) => void;
  setError: (error: string | null) => void;
}

export function useContagemSave(config: ContagemSaveConfig): UseContagemSaveReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Store config in ref to always get fresh values (avoids stale closure)
  const configRef = useRef(config);
  configRef.current = config;

  // #3 (regressão calendário): manter referência fresca do location para
  // propagar o state de origem (from/selectedDate) ao navegar pós-save, sem
  // recriar o callback (evita stale closure, mesmo padrão do configRef).
  const locationRef = useRef(location);
  locationRef.current = location;

  // Epic 46: Estado para modal de validação de intervalo de agendamento
  const [showIntervaloModal, setShowIntervaloModal] = useState(false);
  const [intervaloValidacoes, setIntervaloValidacoes] = useState<ValidarIntervaloResponse[]>([]);
  const [intervaloDataSelecionada, setIntervaloDataSelecionada] = useState('');
  const [intervaloLoading, setIntervaloLoading] = useState(false);
  const [pendingJobData, setPendingJobData] = useState<any>(null);

  // Epic 46: Função para executar o save real (após validação)
  // Epic 47: Aceita justificativas opcionais para incluir no payload
  const executeSave = useCallback(async (
    jobData: any,
    dataPrevista: string,
    justificativas?: JustificativaIntervalo[]
  ) => {
    const { id, isNewContagem, setIsEditMode } = configRef.current;

    // Epic 47: Incluir justificativas no payload se houver
    const finalJobData = justificativas && justificativas.length > 0
      ? {
          ...jobData,
          justificativas_intervalo: justificativas.map(j => ({
            cliente_id: j.cliente_id,
            tipo_violacao: j.tipo_violacao,
            justificativa: j.justificativa,
            intervalo_configurado_dias: j.intervalo_configurado_dias,
            intervalo_atual_dias: j.intervalo_atual_dias,
            data_ultima_contagem: j.data_ultima_contagem,
          })),
        }
      : jobData;

    console.log(isNewContagem ? 'Criando job com dados:' : 'Atualizando job com dados:', finalJobData);

    // Epic 23: Conditional CREATE vs UPDATE
    if (isNewContagem) {
      // CREATE new job
      const novoJob = await jobService.createJob(finalJobData as any);
      console.log('Job criado com sucesso:', novoJob);
      msg.success(MESSAGES.COUNTING.CREATED(novoJob.id));
      // Story 2.61.1: refetchType 'all' refaz o fetch do calendario mesmo
      // estando inativo (refetchOnMount:false). Sem isso, ao navegar pro
      // calendario apos criar/editar/excluir, ele servia cache stale -> F5.
      queryClient.invalidateQueries({ queryKey: ['jobs', 'calendar'], refetchType: 'all' });
      // #3 (regressão calendário): propaga a origem (from/selectedDate) recebida
      // no /new para a página de detalhe, para que o "Voltar" de lá retorne ao
      // calendário no mês agendado. Quando o /new veio da lista (sem state), nada
      // é forçado — mantém o comportamento original (navigate(-1)/lista).
      const incomingState = locationRef.current.state as
        | { from?: string; selectedDate?: string }
        | null;
      navigate(
        `/cloud/contagens/${novoJob.id}`,
        incomingState ? { state: incomingState } : undefined
      );
    } else {
      // UPDATE existing job
      const updatedJob = await jobService.updateJob(parseInt(id!), finalJobData);
      console.log('Job atualizado com sucesso:', updatedJob);
      msg.success(MESSAGES.COUNTING.UPDATED);
      // Story 2.61.1: refetchType 'all' refaz o fetch do calendario mesmo
      // estando inativo (refetchOnMount:false). Sem isso, ao navegar pro
      // calendario apos criar/editar/excluir, ele servia cache stale -> F5.
      queryClient.invalidateQueries({ queryKey: ['jobs', 'calendar'], refetchType: 'all' });
      setIsEditMode(false); // Exit edit mode after successful update
      window.location.reload(); // Reload to show updated data in read-only mode
    }
  }, [navigate]);

  // Epic 46: Handler para confirmação do modal de intervalo (sem justificativas - legado)
  const handleIntervaloConfirm = useCallback(async () => {
    setIntervaloLoading(true);
    try {
      if (pendingJobData) {
        await executeSave(pendingJobData.jobData, pendingJobData.dataPrevista);
      }
    } catch (error: any) {
      console.error('Erro ao salvar após confirmação de intervalo:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      msg.error(MESSAGES.COUNTING.CREATE_ERROR(errorMsg));
    } finally {
      setIntervaloLoading(false);
      setShowIntervaloModal(false);
      setPendingJobData(null);
    }
  }, [executeSave, pendingJobData]);

  // Epic 47: Handler para confirmação do modal de intervalo COM justificativas
  const handleIntervaloConfirmWithJustificativas = useCallback(async (
    justificativas: JustificativaIntervalo[]
  ) => {
    setIntervaloLoading(true);
    try {
      if (pendingJobData) {
        console.log('[Epic 47] Salvando com justificativas:', justificativas);
        await executeSave(pendingJobData.jobData, pendingJobData.dataPrevista, justificativas);
      }
    } catch (error: any) {
      console.error('Erro ao salvar após confirmação de intervalo com justificativas:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      msg.error(MESSAGES.COUNTING.CREATE_ERROR(errorMsg));
    } finally {
      setIntervaloLoading(false);
      setShowIntervaloModal(false);
      setPendingJobData(null);
    }
  }, [executeSave, pendingJobData]);

  // Epic 46: Handler para cancelamento do modal de intervalo
  const handleIntervaloCancel = useCallback(() => {
    setShowIntervaloModal(false);
    setPendingJobData(null);
    setIntervaloValidacoes([]);
    setIntervaloDataSelecionada('');
  }, []);

  const handleSave = useCallback(async () => {
    // Always read from ref to get current values
    const {
      id,
      isNewContagem,
      formData,
      selectedClienteIds,
      selectedHospitalId,
      originalHospitalId,
      modifiedFields,
      originalFormData,
      legacyLocationId,
      auditoresParticipantes,
      locaisContagemSelecionados,
      clienteLocaisPayload,
      allLocaisFlat,
      setIsEditMode,
    } = configRef.current;

    try {
      // Validações
      if (selectedClienteIds.length === 0) {
        msg.warning(MESSAGES.COUNTING.SELECT_CLIENT);
        return;
      }
      if (!selectedHospitalId) {
        msg.warning(MESSAGES.COUNTING.SELECT_ESTABLISHMENT);
        return;
      }
      // REMOVIDO: Validação de local de contagem - agora é opcional
      if (auditoresParticipantes.length === 0) {
        msg.warning(MESSAGES.COUNTING.ADD_AUDITOR);
        return;
      }

      // Converter data de DD/MM/YYYY para YYYY-MM-DD
      const [day, month, year] = formData.dataHora.split('/');
      const dataPrevista = `${year}-${month}-${day}`;

      // EPIC 19/Legacy - Buscar locations do hospital (OPCIONAL)
      // LocalContagem (novo modelo) vs Location (modelo antigo do InventoryJob)
      // Precisamos garantir que location_id não mude se o job não for PRE_AGENDADA
      let locationIdToSave: number | null = null;

      if (isNewContagem) {
        // Novo job: buscar primeiro location disponível
        const locations = await jobService.getLocations(selectedHospitalId);
        locationIdToSave = locations.length > 0 ? locations[0].id : null;
      } else {
        // Job existente
        if (selectedHospitalId === originalHospitalId) {
          // Hospital não mudou: MANTER location_id original (evita erro de validação em status restritos)
          locationIdToSave = legacyLocationId;
        } else {
          // Hospital mudou (só possível em PRE_AGENDADA): buscar novo location
          const locations = await jobService.getLocations(selectedHospitalId);
          locationIdToSave = locations.length > 0 ? locations[0].id : null;
        }
      }

      // Epic LOCAIS-POR-CLIENTE: prefer the new grouped payload when available.
      // Falls back to the legacy flat list when the hook isn't wired up.
      const useLocaisPorCliente = clienteLocaisPayload !== undefined && allLocaisFlat !== undefined;
      const localIdsToSend = useLocaisPorCliente
        ? allLocaisFlat!.map(l => l.id)
        : locaisContagemSelecionados.map(l => l.id);

      // Epic 23: Preparar dados para criação do job
      // NOTA: status NÃO é enviado na criação - backend define como PRE_AGENDADA
      const jobData: any = {
        cliente_ids: selectedClienteIds,
        hospital_id: selectedHospitalId,
        location_id: locationIdToSave, // FK opcional (Legacy/Epic 19)
        operador_id: auditoresParticipantes[0].id, // FK mantido para backward compatibility
        titulo: `Contagem ${formData.dataHora}`,
        descricao: formData.observacoes || 'Contagem criada via Epic 19/23',
        data_prevista: dataPrevista,
        hora_inicio: formData.inicio || (formData as any).hora, // Hora de início (HH:MM)
        hora_fim: formData.termino || null, // Hora de fim (HH:MM); vazio => backend usa default 29min (nao mandar '09:00' bogus que viraria fim<inicio)
        auditor_ids: auditoresParticipantes.map(a => a.id), // Epic 23: M:N auditores
        local_ids: localIdsToSend, // Epic 23 M:N locais (UNION quando Epic LOCAIS-POR-CLIENTE)
        // Aba 2 - Informações da Contagem (Epic 41)
        ordem_coleta: formData.ordemColeta || null,
        lacre_qtd_coleta_manual: formData.lacres || null,
        hora_inicio_contagem: formData.horaInicio || null,
        hora_termino_contagem: formData.horaTermino || null,
        data_envio_relatorio: formData.envioRelatorio || null,
        // Master Override / Cancellation
        status: STATUS_MAP_REVERSE[formData.status] || formData.status,
        motivo_cancelamento: formData.motivoCancelamento || null
        // observacaoDetalhada salva em campo separado se necessário
      };

      // Epic LOCAIS-POR-CLIENTE: include grouped payload when hook is active
      if (useLocaisPorCliente) {
        jobData.cliente_locais = clienteLocaisPayload;
      }

      console.log(isNewContagem ? 'Criando job com dados:' : 'Atualizando job com dados:', jobData);
      console.log('Locais de contagem selecionados (Epic 19):', locaisContagemSelecionados);
      console.log('Location ID usado (sistema antigo):', locationIdToSave);

      // Epic 46: Validar intervalo de agendamento antes de salvar
      //
      // ⛔ N165: esta condição era
      //     if (selectedClienteIds.length > 0 && selectedHospitalId && dataPrevista)
      // — só perguntava se os campos EXISTIAM. Como toda gravação manda a data,
      // TODA gravação reabria o modal de intervalo: incluir lacre, mexer em ordem
      // de coleta, ajustar horário. Alerta que aparece sempre deixa de ser lido, e
      // aí o dia em que ele importa passa batido.
      //
      // Agora quem decide é `clientesParaValidarIntervalo` — a mesma função que
      // governa `useJobForm`, porque este defeito nasceu de a condição existir em
      // dois lugares e só um ser lembrado.
      const clientesAValidar = clientesParaValidarIntervalo({
        isNew: isNewContagem,
        // `dataHora` é o nome do campo da data no formData — o mesmo que a aba de
        // Agendamento usa para pintar a borda de laranja quando muda.
        dataMudou: modifiedFields?.has('dataHora') ?? false,
        hospitalMudou:
          !isNewContagem &&
          originalFormData?.selectedHospitalId != null &&
          selectedHospitalId !== originalFormData.selectedHospitalId,
        clientesAtuais: selectedClienteIds,
        clientesOriginais: originalFormData?.selectedClienteIds ?? [],
      });

      if (clientesAValidar.length > 0 && selectedHospitalId && dataPrevista) {
        try {
          const validacao = await clienteService.validarIntervaloMultiplosClientes(
            clientesAValidar,
            selectedHospitalId,
            dataPrevista,
            isNewContagem ? undefined : parseInt(id!) // Excluir job atual se estiver editando
          );

          // Se houver violações, mostrar modal de alerta e pausar o save
          if (!validacao.valido && validacao.validacoes.some(v => v.violacoes.length > 0)) {
            setIntervaloValidacoes(validacao.validacoes);
            setIntervaloDataSelecionada(dataPrevista);
            setPendingJobData({ jobData, dataPrevista });
            setShowIntervaloModal(true);
            return; // Parar aqui e aguardar decisão do usuário
          }
        } catch (validationError: any) {
          // Se a validação falhar por erro de rede/API, continuar com o save
          console.warn('Erro na validação de intervalo, continuando com save:', validationError);
        }
      }

      // Epic 23: Executar o save diretamente (sem violações ou validação não aplicável)
      await executeSave(jobData, dataPrevista);

    } catch (error: any) {
      console.error('Erro ao criar contagem:', error);

      // Tratar diferentes formatos de erro da API
      let errorMsg = 'Erro desconhecido ao criar contagem';
      const detail = error.response?.data?.detail;

      if (detail) {
        if (typeof detail === 'string') {
          // Erro de negócio (ex: conflito de horário)
          // Simplificar mensagem se for conflito de auditor, mostrando quais auditores
          if (detail.includes('Conflito de horário detectado') || detail.includes('já está alocado')) {
            // Extrair nomes dos auditores do formato: "Auditor 'Nome' já está alocado..."
            const auditorMatches = detail.match(/Auditor '([^']+)'/g);
            const auditorNames = auditorMatches
              ? Array.from(new Set(auditorMatches.map((m: string) => m.match(/Auditor '([^']+)'/)?.[1]).filter(Boolean)))
              : [];

            const auditorList = auditorNames.length > 0
              ? `\n\nAuditor(es) em conflito: ${auditorNames.join(', ')}`
              : '';

            errorMsg = `⚠️ Auditor já alocado em outra contagem no mesmo horário.${auditorList}\n\nPor favor, selecione outro auditor ou altere a data/hora.`;
          } else {
            errorMsg = detail;
          }
        } else if (Array.isArray(detail)) {
          // Erro de validação Pydantic (422)
          const messages = detail.map((err: any) => {
            const field = err.loc?.[err.loc.length - 1] || 'campo';
            return `${field}: ${err.msg}`;
          });
          errorMsg = messages.join('\n');
        } else {
          errorMsg = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      msg.error(MESSAGES.COUNTING.CREATE_ERROR(errorMsg));
    }
  }, [navigate]);

  const handleDelete = useCallback(async (confirm: (opts: any) => Promise<boolean>) => {
    const { id, isNewContagem, formData } = configRef.current;

    if (isNewContagem || !id) {
      msg.warning(MESSAGES.COUNTING.CANNOT_DELETE_UNSAVED);
      return;
    }

    // Confirmacao reforcada para contagem EXECUTADA — apenas MASTER consegue
    // deletar no backend. UI alerta sobre auditoria/log antes de prosseguir.
    const isExecuted = formData?.status === 'EXECUTADA';
    const confirmed = await confirm({
      title: isExecuted
        ? '⚠️ Deletar contagem EXECUTADA?'
        : 'Deletar Contagem',
      message: isExecuted
        ? `Esta contagem foi FINALIZADA (status EXECUTADA) e contém dados de auditoria. ` +
          `Apenas usuários MASTER podem prosseguir. ` +
          `A ação será registrada em log de auditoria. Tem certeza?`
        : MESSAGES.COUNTING.CONFIRM_DELETE(`Contagem ${id}`),
      variant: 'danger',
      confirmText: isExecuted ? 'Sim, deletar EXECUTADA' : 'Deletar',
    });

    if (!confirmed) return;

    try {
      await jobService.deleteJob(parseInt(id));
      msg.success(MESSAGES.COUNTING.DELETED(parseInt(id)));
      // Story 2.61.1: refetchType 'all' refaz o fetch do calendario mesmo
      // estando inativo (refetchOnMount:false). Sem isso, ao navegar pro
      // calendario apos criar/editar/excluir, ele servia cache stale -> F5.
      queryClient.invalidateQueries({ queryKey: ['jobs', 'calendar'], refetchType: 'all' });
      navigate('/cloud/calendario');
    } catch (error: any) {
      console.error('Erro ao deletar contagem:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      msg.error(MESSAGES.COUNTING.DELETE_ERROR(errorMsg));
    }
  }, [navigate]);

  return {
    handleSave,
    handleDelete,
    // Epic 46: Modal de validação de intervalo de agendamento
    showIntervaloModal,
    intervaloValidacoes,
    intervaloDataSelecionada,
    intervaloLoading,
    handleIntervaloConfirm,
    // Epic 47: Handler que aceita justificativas
    handleIntervaloConfirmWithJustificativas,
    handleIntervaloCancel,
  };
}
